import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_ERROR_NOTIFICATION } from '../../../core/error-handling/services/http-error-notification.interceptor';

export interface CalendarioEvento {
  id?: number;
  data?: string;
  dataInicial?: string;
  dataFinal?: string;
  descricao?: string;
  letivo?: boolean;
  diaInteiro?: boolean;
  aceite?: boolean;
  estabelecimentos?: Array<{ id: number }>;
  matrizesCurriculares?: Array<{ id: number }>;
  feriado?: {
    dataFeriado?: string;
  };
}

export interface CalendarioEstabelecimento {
  calendario?: {
    eventos?: CalendarioEvento[];
    feriados?: CalendarioEvento[];
    excecoes?: CalendarioEvento[];
  };
  calendarioSecretaria?: {
    calendario?: {
      eventos?: CalendarioEvento[];
      feriados?: CalendarioEvento[];
      excecoes?: CalendarioEvento[];
    };
  };
  estabelecimento?: {
    id: number;
    nome?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CalendarioService {
  private calendarioCache: Map<number, CalendarioEstabelecimento> = new Map();

  constructor(private http: HttpClient) {}

  async getCalendario(idEstabelecimento: number): Promise<CalendarioEstabelecimento | null> {
    if (this.calendarioCache.has(idEstabelecimento)) {
      return this.calendarioCache.get(idEstabelecimento)!;
    }

    try {
      const url = `${environment.apiUrl}/commons/calendario-estabelecimento/${idEstabelecimento}`;
      const calendario = await firstValueFrom(
        this.http.get<CalendarioEstabelecimento>(url, {
          // Serviço já tem fallback (return null) — suprimir toast global
          context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true)
        })
      );

      this.calendarioCache.set(idEstabelecimento, calendario);
      return calendario;
    } catch (error) {
      return null;
    }
  }

  /**
   * Verifica se uma data é dia letivo pelo calendário (eventos/feriados/exceções).
   * Não considera o quadro de horários (dias da semana com aula).
   */
  verificaDiaLetivo(
    data: Date,
    calendario: CalendarioEstabelecimento | null,
    periodo: { dataInicial: string; dataFinal: string } | null,
    idMatrizTurma?: number
  ): boolean {
    if (!calendario || !periodo) return true; // Se não tem calendário, não bloquear

    const dataStr = this.formatarData(data);

    const excecoes = this.getEventoByTipo('excecoes', calendario, idMatrizTurma)
      .filter(e => e.data === dataStr);

    const eventos = this.getEventoByTipo('eventos', calendario, idMatrizTurma)
      .filter(e => this.isBetween(dataStr, e.dataInicial, e.dataFinal, periodo));

    const feriados = this.getEventoByTipo('feriados', calendario, idMatrizTurma)
      .filter(e => e.feriado?.dataFeriado === dataStr);

    return !this.isDiaNaoLetivo(eventos, feriados, excecoes, data);
  }

  private getEventoByTipo(
    tipoEvento: 'eventos' | 'feriados' | 'excecoes',
    calendario: CalendarioEstabelecimento,
    idMatrizTurma?: number
  ): CalendarioEvento[] {
    let eventos = [...(calendario.calendario?.[tipoEvento] || [])];
    eventos = this.filterEventosByMatriz(eventos, idMatrizTurma);

    let eventosSecretaria = [...(calendario.calendarioSecretaria?.calendario?.[tipoEvento] || [])];
    eventosSecretaria = this.filterEventosByMatriz(eventosSecretaria, idMatrizTurma);

    if (tipoEvento === 'eventos') {
      // Para eventos, filtrar os da secretaria que se aplicam ao estabelecimento
      const eventosSecretariaFiltrados = eventosSecretaria.filter(evento =>
        evento.aceite &&
        (!evento.estabelecimentos || evento.estabelecimentos.length === 0 ||
         evento.estabelecimentos.some(e => e.id === calendario.estabelecimento?.id))
      );
      return eventos.concat(eventosSecretariaFiltrados);
    }

    if (tipoEvento === 'excecoes') {
      return eventos.concat(eventosSecretaria);
    }

    // Para feriados, retornar apenas os da secretaria
    return eventosSecretaria;
  }

  private filterEventosByMatriz(eventos: CalendarioEvento[], idMatrizTurma?: number): CalendarioEvento[] {
    if (!idMatrizTurma) return eventos;
    
    return eventos.filter(evento => {
      const matrizes = evento.matrizesCurriculares || [];
      return matrizes.length === 0 || matrizes.some(m => m.id === idMatrizTurma);
    });
  }

  private isBetween(
    data: string | undefined,
    dataInicial: string | undefined,
    dataFinal: string | undefined,
    periodo: { dataInicial: string; dataFinal: string }
  ): boolean {
    if (!data || !dataInicial || !dataFinal) return false;
    
    const d = new Date(data);
    const inicio = new Date(dataInicial);
    const fim = new Date(dataFinal);
    const inicPeriodo = new Date(periodo.dataInicial);
    const fimPeriodo = new Date(periodo.dataFinal);
    
    return d >= inicio && d <= fim && d >= inicPeriodo && d <= fimPeriodo;
  }

  private isDiaNaoLetivo(
    eventos: CalendarioEvento[],
    feriados: CalendarioEvento[],
    excecoes: CalendarioEvento[],
    data: Date
  ): boolean {
    const qntDiaNaoLetivo =
      this.getQuantidadeDiasNaoLetivos(eventos, true) +
      this.getQuantidadeDiasNaoLetivos(feriados, false) +
      this.getQuantidadeDiasNaoLetivos(excecoes, false);

    const qntDiaLetivo =
      this.getQuantidadeDiasLetivos(eventos) +
      this.getQuantidadeDiasLetivos(feriados) +
      this.getQuantidadeDiasLetivos(excecoes);

    // Evento letivo sobrepõe fim de semana; sem eventos, decisão fica pro quadro de horários.
    return Boolean(qntDiaNaoLetivo > 0);
  }

  private getQuantidadeDiasNaoLetivos(eventos: CalendarioEvento[], verificarDiaInteiro: boolean): number {
    return eventos.filter(e => 
      !e.letivo && (e.diaInteiro || !verificarDiaInteiro)
    ).length;
  }

  private getQuantidadeDiasLetivos(eventos: CalendarioEvento[]): number {
    return eventos.filter(e => e.letivo).length;
  }

  private formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

}
